import { PmpWebService, RestfulPmpWebService } from 'ws/PmpWebService';
import { Pmp, PmpFormType, PmpsWithPagination } from './Pmp';
import { Pageable } from 'ws/Pageable';
import { SelectOptions } from 'components/commonType';
import { isEmpty, omit } from 'lodash';
import { getInitDaypart } from 'components/Dayparts/Dayparts';
import moment from 'moment';

export interface PmpManager {
  getPmp: (pmpId: number) => Promise<Pmp>;
  getPmpList: (pageable: Pageable, search: string, status: number[]) => Promise<PmpsWithPagination>;
  getPmpListOfAgency: (agencyId: number, status: number[]) => Promise<Pmp[]>;
  createPmp: (pmp: PmpFormType) => Promise<void>;
  updatePmp: (pmp: Pmp) => Promise<void>;
  updatePmpState (pmpIds: number[], state: 'activate' | 'deactivate'): Promise<void>;
  getPmpSpaces (): Promise<SelectOptions[]>;
  getPmpActiveSpaces (): Promise<SelectOptions[]>;
  getPmpInactiveSpaces (): Promise<SelectOptions[]>;
  getPmpsWithin (startTime: string, endTime: string): Promise<Pmp[]>;
  getDaypartDescriptions (startTime: string, endTime: string, daypart: {[day: string]: (string | number)[]}): string[];
}

export class DefaultPmpManager implements PmpManager {

  webService: PmpWebService;

  constructor (webService: PmpWebService = new RestfulPmpWebService()) {
    this.webService = webService;
  }

  getPmp (pmpId: number) {
    return this.webService.getPmp(pmpId);
  }

  getPmpList (pageable: Pageable, search: string = '', status: number[] = []) {
    return this.webService.getPmps(pageable, search, status);
  }

  async getPmpListOfAgency (agencyId: number, status: number[] = []) {
    const pmpList = await this.webService.getPmpsOfAgency(agencyId, status);
    return pmpList.filter(pmp => !pmp.campaignId);
  }

  createPmp (pmp: PmpFormType) {
    return this.webService.createPmp(pmp);
  }

  updatePmp (pmp: Pmp) {
    return this.webService.updatePmp(pmp);
  }

  updatePmpState (pmpIds: number[], state: 'activate' | 'deactivate') {
    return this.webService.updatePmpState(pmpIds, state);
  }

  getPmpSpaces () {
    return this.webService.getPmpSpaces();
  }

  getPmpActiveSpaces () {
    return this.webService.getPmpActiveSpaces();
  }

  getPmpInactiveSpaces () {
    return this.webService.getPmpInactiveSpaces();
  }

  getPmpsWithin (startTime: string, endTime: string) {
    return this.webService.getPmpsWithin(startTime, endTime);
  }

  getDaypartDescriptions (startTime: string, endTime: string, daypart: {[day: string]: (string | number)[]}): string[] {
    let targetDaypart = omit(daypart, 'enabled');
    if (isEmpty(targetDaypart)) {
      targetDaypart = getInitDaypart();
    }
    const start = moment(startTime);
    const startString = start.format('YYYY-MM-DD');
    const startDate = moment(startTime).startOf('day');
    const end = moment(endTime);
    const endString = end.format('YYYY-MM-DD');
    const endDate = moment(endTime).startOf('day');
    let descriptions: string[] = [];
    const padStart = (num: number) => num.toString().padStart(2, '0');
    while (startDate.isSameOrBefore(endDate)) {
      const isStartDay = startDate.format('YYYY-MM-DD') === startString;
      const isEndDay = startDate.format('YYYY-MM-DD') === endString;
      const weekday = startDate.weekday();
      const key = weekday === 0 ? 7 : weekday;
      let hours = targetDaypart[key] || targetDaypart[key.toString()];
      if (isStartDay) {
        hours = hours.filter(hour => +hour >= +start.format('HH'));
      }
      if (isEndDay) {
        hours = hours.filter(hour => +hour <= +end.format('HH'));
      }
      const descriptionsOfDate = hours.map(hour => {
        return `${startDate.year()}-${padStart(startDate.month() + 1)}-${padStart(startDate.date())} ${padStart(+hour)}`;
      });
      descriptions = descriptions.concat(descriptionsOfDate);
      startDate.add(1, 'day');
    }
    return descriptions;
  }
}
